import { Cookie } from '@/Cookie';
import { IUserStoreState, IRootState, ILoginCredentials, IUserSession, IUserRegistration, IEmailVerification, IStudentListItem, IResetPasswordVerification } from '@/interfaces';
import { Module } from 'vuex';
import { UserGetters } from '@/Getters';
import { AjaxHelper } from '@/AjaxHelper';

export const userStore: Module<IUserStoreState, IRootState> = {
  state: {
    user: Cookie.getJWT() ?? null
  },
  getters: {
    [UserGetters.getUser]: state => state.user,
    [UserGetters.getRole]: state => state.user?.role ?? '',
    [UserGetters.isAuthenticated]: state => state.user !== null,
    [UserGetters.isSuperAdmin]: state => state.user?.role === 'SuperAdmin',
    [UserGetters.isAdmin]: state => state.user?.role === 'SuperAdmin' || state.user?.role === 'Admin',
    [UserGetters.isParent]: state => state.user?.role === 'Parent'
  },
  mutations: {
    /**
     * Mutations method to update the user in the state of this store
     * 
     * @param state The current state of the store being used
     * @param user The user that should be assigned to the state
     */
    setUser (state, user?: IUserSession | null) {
      state.user = user ?? null;
    }
  },
  actions: {
    /**
     * Method is meant to log into the system
     * 
     * @param commit The commit action that will be sent off to update the state
     * @param creds The credentials that we should use when logging into the api
     */
    async login({ commit, dispatch }, creds: ILoginCredentials) {
      Cookie.clear('ApiSession');
      const token = await AjaxHelper.post<string, ILoginCredentials>('/loginmanagement', creds);
      const expires = new Date();
      expires.setMinutes(expires.getMinutes() + 30000);
      Cookie.set('ApiSession', token, expires);

      // Save the user change to the store
      commit('setUser', Cookie.getJWT());

      // Deal with the cart once we have logged in
      if (!await dispatch('saveCart')) {
        await dispatch('loadCart');
      }
      return true;
    },

    /**
     * Method is meant to register a parent to the system
     * 
     * @param arg1 The argument containing the store to do special stuff to it, but we do not need it
     * @param user The user that we are registering as a parent
     */
    async parentRegister(arg1, user: IUserRegistration) {
      return await AjaxHelper.post<boolean, IUserRegistration>('/parentregistrationmanagement ', user);
    },

    /**
     * Method is meant to send a verification email to the server to unlock the user
     * 
     * @param arg1 The argument containing the store to do special stuff to it, but we do not need it
     * @param verification The verification object that needs to be sent to do an email verification
     */
    async emailVerification(arg1, verification: IEmailVerification) {
      return await AjaxHelper.post<boolean, IEmailVerification>('/emailverification', verification);
    },

    /**
     * Method is meant to verify a password reset
     * 
     * @param arg1 The argument containing the store to do special stuff to it, but we do not need it
     * @param verification The verification object that needs to be sent to reset the password
     */
    async resetPasswordVerification(arg1, verification: IResetPasswordVerification) {
      return await AjaxHelper.post<boolean, IResetPasswordVerification>('/resetpasswordverification', verification);
    },

    /**
     * Method will logout the user
     * 
     * @param commit The commit action that will be sent off to update the state
     */
    async logout({ commit }) {
      Cookie.clear('ApiSession');
      Cookie.clear('CartSession');
      Cookie.clear('CartCoupon');
      commit('setUser');
      commit('setCartItems');
      commit('setCoupon');
      return true;
    }
  }
}